import { useState, useCallback, useRef } from 'react';
import useRecipeV2Api from './useRecipeV2Api';
import { RecipeV2, RecipeV2ListResponse } from '../types';

const useRecipeV2List = () => {
  const api = useRecipeV2Api();
  const [recipes, setRecipes] = useState<RecipeV2[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  // Start in a loading state so the first render does not briefly show an empty/blank table
  // before the initial effect has had a chance to start the request.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const replaceRecipe = useCallback((recipe: RecipeV2) => {
    setRecipes(current => current.map(existing => existing.id === recipe.id ? recipe : existing));
  }, []);

  const loadRecipes = useCallback(async (search: string, status: string, category: string, page: number, pageSize: number) => {
    const currentRequestId = ++requestId.current;
    setIsLoading(true);
    setError(null);
    try {
      const response: RecipeV2ListResponse = await api.getRecipes(search, status, category, page + 1, pageSize);
      if (currentRequestId !== requestId.current) return;
      setRecipes(response.data);
      setTotalCount(response.totalCount);
    } catch (err) {
      if (currentRequestId !== requestId.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load recipes');
      setRecipes([]);
      setTotalCount(0);
    } finally {
      if (currentRequestId === requestId.current) setIsLoading(false);
    }
  }, [api]);

  return { recipes, totalCount, isLoading, error, loadRecipes, replaceRecipe };
};

export default useRecipeV2List;
